import { executeRequest, Request } from '@jfs/network'
import { RootStore } from '../data/store'
import { dateFormatter, DateFormatTypes, getValueFromLOVDescription, isSuccessResponse } from '../utils'
import { APIEndPoints } from './APIEndPoints'
import { getHeaders, getUrl, sitConfig } from './config'

const responseParser = (response: any, requestBody: any) => {
  const data = isSuccessResponse(response)
  return {
    ...data,
    applicationId: requestBody?.applicationId,
    businessName: requestBody?.merchant?.businessDetails?.displayName,
  }
}

const getRiskLimits = (data: any[], existingData: any[] = []) => {
  const extraData =
    existingData.filter((item) => {
      return ![
        'OPEN_LOOP_CURRENT_TRANSACTION_LIMIT',
        'OPEN_LOOP_CURRENT_EXIT_LIMIT',
        'OPEN_LOOP_CURRENT_TRANSACTION_COUNT',
      ].includes(item?.key)
    }) ?? []
  const existingTransactionLimit = existingData.find((item) => item.key === 'OPEN_LOOP_CURRENT_TRANSACTION_LIMIT')
  const existingExitLimit = existingData.find((item) => item.key === 'OPEN_LOOP_CURRENT_EXIT_LIMIT')
  const existingTransactionCount = existingData.find((item) => item.key === 'OPEN_LOOP_CURRENT_TRANSACTION_COUNT')
  if (data?.find((item) => item && item?.length > 0)) {
    const updatedTransactionData = [
      ...(data?.[0]
        ? [
          {
            ...existingTransactionLimit,
            configType: 'OPEN_LOOP_RISK',
            key: 'OPEN_LOOP_CURRENT_TRANSACTION_LIMIT',
            value: data?.[0],
          },
        ]
        : existingTransactionLimit
          ? [existingTransactionLimit]
          : []),
      ...(data?.[1]
        ? [
          {
            ...existingExitLimit,
            configType: 'OPEN_LOOP_RISK',
            key: 'OPEN_LOOP_CURRENT_EXIT_LIMIT',
            value: data?.[1],
          },
        ]
        : existingExitLimit
          ? [existingExitLimit]
          : []),
      ...(data?.[2]
        ? [
          {
            ...existingTransactionCount,
            configType: 'OPEN_LOOP_RISK',
            key: 'OPEN_LOOP_CURRENT_TRANSACTION_COUNT',
            value: data?.[2],
          },
        ]
        : existingTransactionCount
          ? [existingTransactionCount]
          : []),
      ...extraData,
    ]
    return updateExistingConfigMap(existingData, updatedTransactionData, 'key')
  }
  return undefined
}

const getRiskConfigMap = (data: any[], metaData: any, existingData: any[] = []) => {
  const extraData =
    existingData.filter((item) => {
      return ![
        'DELIVERY_PERIOD',
        'MERCHANT_DAY_DELIVERY',
        'INTERNATIONAL_ACCEPTANCE',
        'MERCHANT_RISK',
        'ALLOW_PARENT_ID_SETTL_FLAG',
      ].includes(item?.key)
    }) ?? []
  const existingDeliveryPeriod = existingData?.find((item) => item.key === 'DELIVERY_PERIOD')
  const existingInternationalAcceptance = existingData?.find((item) => item.key === 'INTERNATIONAL_ACCEPTANCE')
  const existingDefferedDeliveryOption = existingData?.find((item) => item.key === 'MERCHANT_DAY_DELIVERY')
  const existingRiskCategoryOption = existingData?.find((item) => item.key === 'MERCHANT_RISK')
  const allowParentIdSettlement = existingData?.find((item) => item.key === 'ALLOW_PARENT_ID_SETTL_FLAG')
  const deliveryMode = getValueFromLOVDescription(metaData?.lovData, data[0], 'DeliveryMode')
  if (data?.find((item) => item && item?.length > 0)) {
    const updateRiskConfigData = [
      ...(data[0]
        ? [
          {
            ...existingDeliveryPeriod,
            configType: 'RISK_SETTING',
            key: 'DELIVERY_PERIOD',
            value: deliveryMode,
          },
        ]
        : existingDeliveryPeriod
          ? [existingDeliveryPeriod]
          : []),
      ...(deliveryMode !== 'I' && data[1]
        ? [
          {
            ...existingDefferedDeliveryOption,
            configType: 'RISK_SETTING',
            key: 'MERCHANT_DAY_DELIVERY',
            value: getValueFromLOVDescription(metaData?.lovData, data[1], 'DeferredDeliveryOptions'),
          },
        ]
        : existingDefferedDeliveryOption
          ? [existingDefferedDeliveryOption]
          : []),
      ...(data[2]
        ? [
          {
            ...existingInternationalAcceptance,
            configType: 'RISK_SETTING',
            key: 'INTERNATIONAL_ACCEPTANCE',
            value: data[2] === 'Yes' ? 'Y' : 'N',
          },
        ]
        : existingInternationalAcceptance
          ? [existingInternationalAcceptance]
          : []),
      ...(data[3]
        ? [
          {
            ...existingRiskCategoryOption,
            configType: 'RISK_SETTING',
            key: 'MERCHANT_RISK',
            value: data[3],
          },
        ]
        : existingRiskCategoryOption
          ? [existingRiskCategoryOption]
          : []),
      ...(data[4]
        ? [
          {
            ...allowParentIdSettlement,
            configType: 'RISK_SETTING',
            key: 'ALLOW_PARENT_ID_SETTL_FLAG',
            value: data[4] === 'Yes' ? 'Y' : 'N',
          },
        ]
        : allowParentIdSettlement
          ? [allowParentIdSettlement]
          : []
      ),
      ...extraData,

    ]
    return updateExistingConfigMap(existingData, updateRiskConfigData, 'key')
  }
  return undefined
}

const updateExistingConfigMap = (existingData, updateRiskConfigData, field) => {
  // Create a map from array1 based on the common field for quick lookup of order
  const orderMap = new Map(existingData.map(item => [item[field], item]));

  // Separate elements from array2 that exist in array1 and those that don't
  const matched = [];
  const extras = [];

  updateRiskConfigData.forEach(item => {
    if (orderMap.has(item[field])) {
      matched.push(item);
    } else {
      extras.push(item);
    }
  });

  // Sort the matched array based on the order in array1
  matched.sort((a, b) => {
    return existingData.findIndex(item => item[field] === a[field]) - existingData.findIndex(item => item[field] === b[field]);
  });

  // Return the matched elements in order followed by any extra elements
  console.log("HTAG updateExistingConfigMap", [...matched, ...extras])
  return [...matched, ...extras];
}

export function getFormattedDate() {
  const date = new Date()

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  const milliseconds = String(date.getMilliseconds()).padStart(3, '0')

  return `${year}-${month}-${day}-${hours}-${minutes}-${seconds}-${milliseconds}`
}

const getDocumentUploadId = (onBordingData, uploadId) =>
  onBordingData?.authSignatoryPoi_docs_data?.length > 0 ||
    onBordingData?.authSignatoryPoA_docs_data?.length > 0 ||
    onBordingData?.businessDetailsPob_docs_data?.length > 0 ||
    onBordingData?.canceled_check_docs_data?.length > 0 ||
    onBordingData?.doc_upload_docs_data?.length > 0 ||
    onBordingData?.risk_supporting_docs_data?.length > 0
    ? uploadId
    : undefined

const getFormattedJDMSReq = (docs = []) => {
  console.log('getFormattedJDMSReq docs', docs)

  return docs?.map((item) => ({
    documentUploadId: undefined,
    id: item?.id,
    documentCategory: item?.documentCategory, //found
    documentType: RootStore?.documentCategories?.find((doc) => doc.value === item?.documentId)?.key ?? 'NA', //found
    fileName: item?.kycDocuments?.[0]?.NAME ?? '',
    documentId: item?.documentId,
    number: item?.documentNumber + '', //found
    documentName: item?.MERCHANT_DOCUMENT_NAME,
    expiryDate: dateFormatter(item?.dateOfExpiry, DateFormatTypes.DATE_ONLY_WITHOUT_TIME_REVERSE, ''),
    createdAt: undefined,
    updatedAt: undefined,
    merchantDocumentType: item?.kycDocuments?.[0]?.MERCHANT_DOCUMENT_TYPE ?? '',
  }))
}

export const getRequestFromEntityFormData = (onboardingData: any, metadata: any, dedupeData: any) => {
  console.log('createRequest ~ metadata:', metadata)
  console.log('createRequest ~ onboardingData:', onboardingData)
  return {
    applicationId: `NEW-${getFormattedDate()}`,
    realtimeDedupeSearchResponse: dedupeData,
    merchant: {
      parentEntityIdentificationNumber: onboardingData?.parentEntityId,
      groupId: onboardingData?.parentGroupId,
      subGroupId: onboardingData?.parentSubGroupId,
      type: onboardingData?.merchantType,
      onboardingChannel: getValueFromLOVDescription(metadata?.lovData, onboardingData?.status, 'SetupCompleteInd'),
      entityIdentificationNumber: onboardingData?.entityIdentificationNumber
        ? onboardingData?.entityIdentificationNumber
        : undefined,
      primaryContactDetail: {
        mobileNumber: {
          countryCode: '+91',
          mobileNumber: onboardingData?.personalMobileNumber,
        },
        emailId: onboardingData?.personalEmailId,
        name: {
          firstName: onboardingData?.personalFirstName,
          lastName: onboardingData?.personalLastName,
        },
      },
      status: {
        statusCode: getValueFromLOVDescription(metadata?.lovData, onboardingData?.status, 'Status'),
        reasonCode: getValueFromLOVDescription(metadata?.lovData, onboardingData?.reasonCode, 'ReasonCode'),
        reasonMessage: onboardingData?.reasonMessage,
      },
      authorizedSignatoryList: [
        {
          authorisedSignatoryReferenceId: onboardingData?.authorisedSignatoryReferenceId,
          name: {
            firstName: onboardingData?.authSignatoryFirstName,
            middleName: undefined,
            lastName: onboardingData?.authSignatoryLastName,
          },
          dateOfBirth: dateFormatter(
            onboardingData?.authSignatoryDateOfBirth,
            DateFormatTypes.DATE_ONLY_WITHOUT_TIME_REVERSE,
          ),
          gender: getValueFromLOVDescription(metadata?.lovData, onboardingData?.authSignatoryGender, 'Gender'),
          address: {
            id: onboardingData?.authorisedSignatoryAddressId,
            addressType: 'Residential',
            locationOwnership: undefined,
            addressLine1: onboardingData?.authSignatoryAddressLine1,
            addressLine2: onboardingData?.authSignatoryAddressLine2,
            pinCode: onboardingData?.authSignatoryPincode,
            city: onboardingData?.authSignatoryCity,
            state: getValueFromLOVDescription(metadata?.lovData, onboardingData?.authSignatoryState, 'State'),
            country: 'INDIA',
          },
          poi: onboardingData?.authSignatoryPoi_docs_data
            ?.map((item) => ({
              documentUploadId: metadata?.uploadId,
              id: undefined,
              documentId: item?.RID,
              number: item?.documentNumber,
              documentName: item?.MERCHANT_DOCUMENT_NAME,
              documentType: metadata?.poiData?.find((poi) => poi.value == item?.MERCHANT_DOCUMENT_NAME)?.key ?? 'NA',
              documentCategory: 'POI',
              fileName: item?.NAME,
              expiryDate: dateFormatter(item?.expiry, DateFormatTypes.DATE_ONLY_WITHOUT_TIME_REVERSE, ''),
              createdAt: undefined,
              updatedAt: undefined,
            }))
            ?.concat(getFormattedJDMSReq(onboardingData?.authSignatoryPoi_docs_data_jdms)),
          poa:
            onboardingData?.authSignatoryPoA_docs_data
              ?.map((item) => ({
                documentUploadId: metadata?.uploadId,
                id: undefined,
                documentId: item?.RID,
                number: item?.documentNumber,
                documentName: item?.MERCHANT_DOCUMENT_NAME,
                documentType: metadata?.poaData?.find((poa) => poa.value == item?.MERCHANT_DOCUMENT_NAME)?.key ?? 'NA',
                documentCategory: 'POA',
                fileName: item?.NAME,
                expiryDate: dateFormatter(item?.expiry, DateFormatTypes.DATE_ONLY_WITHOUT_TIME_REVERSE, ''),
                createdAt: undefined,
                updatedAt: undefined,
              }))
              ?.concat(getFormattedJDMSReq(onboardingData?.authSignatoryPoA_docs_data_jdms)) ?? [],
        },
      ],
      businessDetails: {
        id: onboardingData?.businessDetailsId,
        businessChannel: getValueFromLOVDescription(
          metadata?.lovData,
          onboardingData?.businessChannel,
          'BusinessChannel',
        ),
        businessType: getValueFromLOVDescription(metadata?.lovData, onboardingData?.entityType, 'OwnerType'),
        businessOwnershipType: getValueFromLOVDescription(
          metadata?.lovData,
          onboardingData?.ownershipType,
          'OwnershipType',
        ),
        storeCodeDetail: onboardingData?.storeCode
          ? {
              storeCode: onboardingData?.storeCode,
              format: onboardingData?.format,
            }
          : undefined,
        displayName: onboardingData?.businessDisplayName,
        legalName: onboardingData?.businessLegalName,
        businessCategory: onboardingData?.mccCodeComponent?.businessCategory,
        businessLine: onboardingData?.mccCodeComponent?.businessLine?.value,
        mcc: {
          mcc: onboardingData?.mccCodeComponent?.mccCode,
          description: onboardingData?.mccCodeComponent?.businessCategory,
        },
        riskCategory: undefined,
        yearsInBusiness: getValueFromLOVDescription(
          metadata?.lovData,
          onboardingData?.yearsOfBusiness,
          'YearsInBusiness',
        ),
        businessPan: onboardingData?.businessPan,
        businessAddressType: getValueFromLOVDescription(metadata?.lovData, onboardingData?.premiseType, 'PremiseType'),
        storeAddressType: getValueFromLOVDescription(metadata?.lovData, onboardingData?.storeType, 'StoreType'),
        businessAddress: {
          id: onboardingData?.businessDetailsAddressId,
          addressType: 'Commercial',
          addressLine1: onboardingData?.addressLine1,
          addressLine2: onboardingData?.addressLine2,
          pinCode: onboardingData?.pincode,
          city: onboardingData?.city,
          state: getValueFromLOVDescription(metadata?.lovData, onboardingData?.state, 'State'),
          country: 'INDIA',
        },
        cin: onboardingData?.cinNumber,
        incorporatedDate: onboardingData?.companyIncorporatedDate
          ? dateFormatter(onboardingData?.companyIncorporatedDate, DateFormatTypes.DATE_ONLY_WITHOUT_TIME_REVERSE)
          : undefined,
        pob: onboardingData?.businessDetailsPob_docs_data
          ?.map((item) => ({
            documentUploadId: metadata?.uploadId,
            id: undefined,
            documentId: item?.RID,
            number: item?.documentNumber,
            documentName: item?.MERCHANT_DOCUMENT_NAME,
            documentType: metadata?.pobData?.find((pob) => pob.value == item?.MERCHANT_DOCUMENT_NAME)?.key ?? 'NA',
            documentCategory: 'POB',
            fileName: item?.NAME,
            expiryDate: dateFormatter(item?.expiry, DateFormatTypes.DATE_ONLY_WITHOUT_TIME_REVERSE, ''),
            createdAt: undefined,
            updatedAt: undefined,
          }))
          ?.concat(getFormattedJDMSReq(onboardingData?.businessDetailsPob_docs_data_jdms)),
      },
      otherBusinessDetails: {
        website: onboardingData?.businessWebsite,
        landlineNumber: onboardingData?.landlineNumber,
        emailId: onboardingData?.businessEmailId,
        dailyTransactionCount: getValueFromLOVDescription(
          metadata?.lovData,
          onboardingData?.dailyTransactionCount,
          'DailyTransactionVolume',
        )||onboardingData?.dailyTransactionCount,
        dailyTransactionValue: getValueFromLOVDescription(
          metadata?.lovData,
          onboardingData?.dailyTransactionValue,
          'DailyTransactionValue',
        )|| onboardingData?.dailyTransactionValue,
        //returnPolicy: getValueFromLOVDescription(metadata?.lovData, onboardingData?.returnPolicy, 'ReturnPolicy'),
      },
      gstDetails: {
        id: onboardingData?.gstDetailsId,
        category: getValueFromLOVDescription(metadata?.lovData, onboardingData?.gstCategory, 'MerchantCategory'),
        number: onboardingData?.gstinNumber,
        state: undefined,
        dealerCategory: getValueFromLOVDescription(
          metadata?.lovData,
          onboardingData?.gstDealerCategory,
          'GSTDealerCategory',
        ),
        typeOfBusiness: getValueFromLOVDescription(metadata?.lovData, onboardingData?.typeOfBusiness, 'BusinessType'),
      },
      settlementDetail: {
        id: onboardingData?.settlementDetailId,
        beneficiaryName: onboardingData?.bankAccountHolderName,
        accountNumber: onboardingData?.settlementAccountNumber,
        ifscCode: onboardingData?.ifscCode,
        bankName: undefined,
        bankCity: undefined,
        settlementFrequency: onboardingData?.settlementFrequency,
        settlementPlanId: onboardingData?.settlementPlanId,
        documents: onboardingData?.canceled_check_docs_data
          ?.map((item) => ({
            documentUploadId: metadata?.uploadId,
            id: undefined,
            documentId: item?.RID,
            number: item?.documentNumber,
            documentName: item?.MERCHANT_DOCUMENT_NAME,
            documentType:
              onboardingData?.docMetaData?.find((doc) => doc.value == item?.MERCHANT_DOCUMENT_NAME)?.key ?? 'NA',
            documentCategory: item?.MERCHANT_DOCUMENT_TYPE,
            fileName: item?.NAME,
            expiryDate: undefined,
            createdAt: undefined,
            updatedAt: undefined,
          }))
          .concat(getFormattedJDMSReq(onboardingData?.canceled_check_docs_data_jdms)),
      },
      feePackage: {
        id: onboardingData?.feePackageReferenceId,
        feePackageId: onboardingData?.feePackageId,
        inheritParentFeePackage: onboardingData?.allowParentFeePackageId === 'yes',
        programs: onboardingData?.listOfPrograms
          ? [
            {
              programId: onboardingData?.listOfPrograms,
              programReferenceId: onboardingData?.referenceId,
            },
          ]
          : [],
      },
      riskSettings: {
        limits: getRiskLimits(
          [
            onboardingData?.riskCurrentTnxLimit,
            onboardingData?.riskCurrentExitLimit,
            onboardingData?.riskCurrentTnxCount,
          ],
          onboardingData?.riskSettings?.limits,
        ),
        configMap: getRiskConfigMap(
          [
            onboardingData?.riskDeliveryPeriod,
            onboardingData?.riskDeferredDeliveryOptions,
            onboardingData?.riskInternationalAcceptance,
            onboardingData?.merchantRiskCategory,
            onboardingData?.allowParentIdSettlement,
          ],
          metadata,
          onboardingData?.riskSettings?.configMap,
        ),
        documents: onboardingData?.risk_supporting_docs_data
          ? onboardingData?.risk_supporting_docs_data
            .map((item) => ({
              documentUploadId: metadata?.uploadId,
              id: undefined,
              documentId: item?.RID,
              number: item?.documentNumber,
              documentName: item?.MERCHANT_DOCUMENT_NAME,
              documentType:
                onboardingData?.docMetaData?.find((doc) => doc.value == item?.MERCHANT_DOCUMENT_NAME)?.key ?? 'NA',
              documentCategory: item?.MERCHANT_DOCUMENT_TYPE,
              fileName: item?.NAME,
              expiryDate: dateFormatter(item?.expiry, DateFormatTypes.DATE_ONLY_WITHOUT_TIME_REVERSE, ''),
              createdAt: undefined,
              updatedAt: undefined,
            }))
            .concat(getFormattedJDMSReq(onboardingData?.risk_supporting_docs_data_jdms))
          : undefined,
      },

      documentUploadId: getDocumentUploadId(onboardingData, metadata?.uploadId),

      documents: onboardingData?.doc_upload_docs_data
        ? onboardingData?.doc_upload_docs_data
          .map((item) => ({
            documentUploadId: metadata?.uploadId,
            id: undefined,
            documentId: item?.RID,
            number: item?.documentNumber,
            documentName: item?.MERCHANT_DOCUMENT_NAME,
            documentType:
              onboardingData?.docMetaData?.find((doc) => doc.value == item?.MERCHANT_DOCUMENT_NAME)?.key ?? 'NA',
            documentCategory: item?.MERCHANT_DOCUMENT_TYPE,
            fileName: item?.NAME,
            expiryDate: dateFormatter(item?.expiry, DateFormatTypes.DATE_ONLY_WITHOUT_TIME_REVERSE, ''),
            createdAt: undefined,
            updatedAt: undefined,
          }))
          .concat(getFormattedJDMSReq(onboardingData?.doc_upload_docs_data_jdms))
        : undefined,
      pgConfigs: undefined,
      terminalId: undefined,
      configMap: onboardingData?.configMap,
      configList: onboardingData?.configList,
    },
    opsMakerActionType: 'SUBMIT',
    makerDedupeSearchResponse: {},
    skipDedupe: false,
  }
}

export const eosCreateWorkItem: any = (onboardingData: any, metaData: any, dedupeData: any, tokens: any) => {
  const requestBody = getRequestFromEntityFormData(onboardingData, metaData, dedupeData)
  const request: Request<{}> = {
    apiPath: getUrl(APIEndPoints.eosCreateWorkItem),
    method: 'POST',
    config: sitConfig(tokens),
    headers: getHeaders(),
    body: requestBody,
  }
  return executeRequest(request).then((data) => {
    return responseParser(data, requestBody)
  })
}
